import "./quote-details-summary.css";

import { Label } from "@tql/ui-components-react/kendo/labels";
import { format } from "date-fns";
import { useContext } from "react";

import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useSelectedAccessorials } from "~/features/quote/hooks/use-selected-accessorials.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { SelectedAccessorials } from "../../../shared/selected-accessorials.tsx";

export const QuoteDetailsSummary = () => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();

	const getClass = (): string => {
		if (quoteState.items.length === 0) {
			return "";
		}

		const uniqueClasses = new Set(
			quoteState.items.map((item) => item.class.code),
		);

		if (uniqueClasses.size === 1) {
			const [firstClass] = uniqueClasses;
			return firstClass ?? "";
		}

		return "Multiple Classes";
	};

	const totals = { weight: 0 };
	for (const item of quoteState.items) {
		const weight = item.weight ?? 0;
		// const quantity = item.quantity ?? 0;
		totals.weight += weight;
	}
	const allSelectedAccessorials = useSelectedAccessorials();

	return (
		<div className={"quote-details-summary-wrapper"}>
			<div className={"quote-details-summary-ship-date"}>
				<Label>Ship Date</Label>
				<div>{format(new Date(quoteState.shipmentDate), "MM/dd/yyyy")}</div>
			</div>
			<div className={"quote-details-summary-pickup"}>
				<Label>Pick up</Label>
				<div>
					{[
						selectedAddressBookItems.pickup?.city ||
							quoteState.pickupPostalCode.city?.name ||
							"",
						selectedAddressBookItems.pickup?.stateProvince ||
							quoteState.pickupPostalCode.city?.state?.abbreviation ||
							"",
						selectedAddressBookItems.pickup?.postalCode ||
							quoteState.pickupPostalCode.postalCode ||
							"",
					]
						.filter(Boolean)
						.join(" ")}
				</div>
			</div>
			<div className={"quote-details-summary-delivery"}>
				<Label>Delivery</Label>
				<div>
					{[
						selectedAddressBookItems.delivery?.city ||
							quoteState.deliveryPostalCode.city?.name ||
							"",
						selectedAddressBookItems.delivery?.stateProvince ||
							quoteState.deliveryPostalCode.city?.state?.abbreviation ||
							"",
						selectedAddressBookItems.delivery?.postalCode ||
							quoteState.deliveryPostalCode.postalCode ||
							"",
					]
						.filter(Boolean)
						.join(" ")}
				</div>
			</div>
			<div className={"quote-details-summary-accessorials"}>
				<Label>Accessorial Services</Label>
				<SelectedAccessorials accessorials={allSelectedAccessorials} />
			</div>

			{quoteState.insuranceValue || quoteState.insuranceProductTypeId ? (
				<div
					className={
						"quote-details-summary-right-column quote-details-summary-insurance-column"
					}
				>
					{quoteState.insuranceValue ? (
						<div
							className={"quote-details-summary-total-item"}
							data-summary-field={"shipment-value"}
						>
							<Label>Shipment Value</Label>
							<div className={"quote-details-summary-total-value"}>
								{formatCurrency(quoteState.insuranceValue ?? 0)}
							</div>
						</div>
					) : null}
					{quoteState.insuranceProductTypeId ? (
						<div
							className={"quote-details-summary-total-item"}
							data-summary-field={"product"}
						>
							<Label>Product</Label>
							<div className={"quote-details-summary-total-value"}>
								{quoteState.insuranceProductTypeId == 1 ? "New" : "Used"}
							</div>
						</div>
					) : null}
				</div>
			) : null}

			<div
				className={
					"quote-details-summary-right-column quote-details-summary-totals-column"
				}
			>
				<div
					className={"quote-details-summary-total-item"}
					data-summary-field={"total-weight"}
				>
					<Label>Total Weight</Label>
					<div className={"quote-details-summary-total-value"}>
						{totals.weight.toLocaleString()} lbs
					</div>
				</div>
				<div
					className={"quote-details-summary-total-item"}
					data-summary-field={"total-linear-feet"}
				>
					<Label>Total Linear Feet</Label>
					<div className={"quote-details-summary-total-value"}>
						{Math.round(
							quoteState.customTotalLinearFeet > 0
								? quoteState.customTotalLinearFeet
								: quoteState.totalLinearFeet,
						).toLocaleString()}{" "}
						ft
					</div>
				</div>
				<div
					className={"quote-details-summary-total-item"}
					data-summary-field={"class"}
				>
					<Label>Class</Label>
					<div className={"quote-details-summary-total-value"}>
						{getClass()}
					</div>
				</div>
			</div>
		</div>
	);
};
